/**
 * Core Event Bus — Zero-allocation subscriber architecture
 * Handles subsystem communication via fixed event IDs and typed data payloads.
 */

export enum EventType {
  // Engine Lifecycle
  ENGINE_READY = 0x0001,
  ENGINE_PAUSE = 0x0002,
  ENGINE_RESUME = 0x0003,
  
  // Scene Progression
  SCENE_CHANGE_REQUEST = 0x0010,
  SCENE_TRANSITION_START = 0x0011,
  SCENE_TRANSITION_COMPLETE = 0x0012,

  // Gesture & Input
  INPUT_POINTER_DOWN = 0x0020,
  INPUT_POINTER_MOVE = 0x0021,
  INPUT_POINTER_UP = 0x0022,
  INPUT_GESTURE_STRIKE = 0x0023,
  INPUT_GESTURE_DRAG = 0x0024,
  INPUT_GESTURE_ORBIT = 0x0025,

  // Subsystem Actions & Audio Triggers
  AUDIO_TRIGGER_SPARK = 0x0030,
  AUDIO_TRIGGER_FRICTION = 0x0031,
  AUDIO_TRIGGER_BELL = 0x0032,
  AUDIO_TRIGGER_IMPLOSION = 0x0033,

  // Telemetry & Physics
  PHYSICS_COLLISION = 0x0040,
  TELEMETRY_FPS_UPDATE = 0x0050,
  ACCESSIBILITY_ANNOUNCE = 0x0060
}

export interface EventPayload {
  eventId: EventType;
  timestamp: number; // microsecond precision
  x?: number;
  y?: number;
  dx?: number;
  dy?: number;
  velocity?: number;
  intensity?: number;
  sceneId?: number;
  text?: string;
  data?: any;
}

export type EventCallback = (payload: EventPayload) => void;

export class EventBus {
  private static instance: EventBus;
  private subscribers: Map<EventType, EventCallback[]>;
  private reusablePayload: EventPayload;

  private constructor() {
    this.subscribers = new Map();
    this.reusablePayload = {
      eventId: EventType.ENGINE_READY,
      timestamp: 0
    };
  }

  public static getInstance(): EventBus {
    if (!EventBus.instance) {
      EventBus.instance = new EventBus();
    }
    return EventBus.instance;
  }

  public subscribe(eventType: EventType, callback: EventCallback): () => void {
    if (!this.subscribers.has(eventType)) {
      this.subscribers.set(eventType, []);
    }
    const handlers = this.subscribers.get(eventType)!;
    handlers.push(callback);

    // Return unsubscribe function
    return () => {
      const index = handlers.indexOf(callback);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    };
  }

  public emit(
    eventType: EventType,
    x = 0,
    y = 0,
    dx = 0,
    dy = 0,
    velocity = 0,
    intensity = 1.0,
    sceneId = 0,
    text = '',
    data: any = null
  ): void {
    const handlers = this.subscribers.get(eventType);
    if (!handlers || handlers.length === 0) return;

    // Use zero-allocation reusable payload object
    this.reusablePayload.eventId = eventType;
    this.reusablePayload.timestamp = performance.now();
    this.reusablePayload.x = x;
    this.reusablePayload.y = y;
    this.reusablePayload.dx = dx;
    this.reusablePayload.dy = dy;
    this.reusablePayload.velocity = velocity;
    this.reusablePayload.intensity = intensity;
    this.reusablePayload.sceneId = sceneId;
    this.reusablePayload.text = text;
    this.reusablePayload.data = data;

    for (let i = 0; i < handlers.length; i++) {
      handlers[i](this.reusablePayload);
    }
  }

  public clear(): void {
    this.subscribers.clear();
  }
}
